import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bank, CheckCircle, XCircle, PlugsConnected } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "./ui/button";

export const GatewayFiscaleCard = () => {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [verifying, setVerifying] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [soglia, setSoglia] = useState("");
  const [savingSoglia, setSavingSoglia] = useState(false);

  useEffect(() => {
    api.get("/fiscale/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
    api.get("/fiscale/log").then((r) => setLogs(r.data.chiamate || [])).catch(() => {});
    api.get("/menu/saldo-soglia").then((r) => setSoglia(r.data.soglia ?? "")).catch(() => {});
  }, []);

  const saveSoglia = async () => {
    setSavingSoglia(true);
    try {
      const v = soglia === "" ? null : Number(soglia);
      await api.post("/menu/saldo-soglia", { soglia: v });
      toast.success(v ? `Soglia salvata: € ${v}` : "Avviso saldo basso disattivato");
    } catch (e) {
      toast.error("Salvataggio soglia non riuscito");
    } finally {
      setSavingSoglia(false);
    }
  };

  const verify = async () => {
    setVerifying(true);
    setCheckResult(null);
    try {
      const { data } = await api.get("/fiscale/status", { params: { verify: 1 } });
      setStatus(data);
      setCheckResult(data.collegamento === "ok"
        ? { ok: true, msg: "Collegamento ad A-Cube riuscito: token ottenuto." }
        : { ok: false, msg: data.collegamento_dettaglio || "Collegamento fallito." });
    } catch (e) {
      setCheckResult({ ok: false, msg: formatApiErrorDetail(e) });
      toast.error("Verifica collegamento fallita");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="p-6 sm:p-8 rounded-2xl border border-white/10 bg-white/[0.02] mt-8 animate-fade-up" data-testid="gateway-fiscale-card">
      <div className="flex items-center gap-3 mb-1">
        <Bank size={32} weight="duotone" className="text-emerald-400" />
        <h2 className="font-heading text-3xl font-bold tracking-tight">Gateway Fiscale</h2>
        {status && (
          <span data-testid="gateway-fiscale-env" className={`ml-2 px-2.5 py-0.5 rounded-full text-[11px] font-bold tracking-wide ${status.environment === "production" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>
            {status.environment === "production" ? "PRODUZIONE" : "SANDBOX"}
          </span>
        )}
      </div>
      <p className="text-sm text-slate-400 mb-6">API A-Cube centralizzate per tutto l'ecosistema: fatture SDI, Open Banking, Peppol/NSO e conservazione a norma. Le sub-app le usano senza mai vedere le credenziali.</p>

      {status ? (
        <>
          <div className="flex items-center gap-2 mb-4 text-sm" data-testid="gateway-fiscale-credenziali">
            {status.credenziali_configurate
              ? <><CheckCircle size={18} weight="fill" className="text-emerald-400" /><span className="text-slate-300">Credenziali A-Cube configurate</span></>
              : <><XCircle size={18} weight="fill" className="text-red-400" /><span className="text-slate-300">Credenziali A-Cube mancanti</span></>}
            <Button data-testid="gateway-fiscale-verify-btn" onClick={verify} disabled={verifying}
              className="ml-auto h-9 px-4 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-xs">
              <PlugsConnected size={14} className="mr-1.5" />
              {verifying ? "Verifica in corso…" : "Verifica collegamento"}
            </Button>
          </div>
          {checkResult && (
            <div data-testid="gateway-fiscale-check-result" className={`mb-4 px-3 py-2 rounded-md text-xs ${checkResult.ok ? "bg-emerald-500/10 text-emerald-300" : "bg-red-500/10 text-red-300"}`}>
              {checkResult.msg}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {status.famiglie.map((f) => (
              <div key={f.chiave} data-testid={`gateway-fiscale-fam-${f.chiave}`} className="p-4 rounded-lg bg-surface border border-surface-border">
                <div className="text-sm font-semibold text-white">{f.label}</div>
                <div className="text-[11px] text-slate-500 mt-1 font-mono">/api/fiscale/{f.chiave}/… → {f.base_url}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 p-4 rounded-lg bg-surface border border-surface-border flex flex-wrap items-center gap-3" data-testid="gateway-fiscale-soglia">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white">Avviso saldo basso</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Se il saldo complessivo dei conti collegati scende sotto questa soglia, i Master ricevono un avviso in dashboard (max 1 al giorno). Vuoto = disattivato.</div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-slate-400">€</span>
              <input data-testid="gateway-fiscale-soglia-input" type="number" min="0" step="100" value={soglia}
                onChange={(e) => setSoglia(e.target.value)} placeholder="es. 5000"
                className="w-28 h-9 px-3 rounded-md bg-black/30 border border-white/15 text-sm text-white focus:outline-none focus:border-emerald-400/60" />
              <Button data-testid="gateway-fiscale-soglia-save" onClick={saveSoglia} disabled={savingSoglia}
                className="h-9 px-4 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-xs">
                {savingSoglia ? "Salvo…" : "Salva"}
              </Button>
            </div>
          </div>
          {logs.length > 0 && (
            <div className="mt-5">
              <div className="text-xs tracking-[0.2em] uppercase text-white/60 mb-2">Ultime chiamate</div>
              <div className="space-y-1 max-h-44 overflow-y-auto" data-testid="gateway-fiscale-log">
                {logs.map((l, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px] font-mono text-slate-400">
                    <span className={l.status < 400 ? "text-emerald-400" : "text-red-400"}>{l.status}</span>
                    <span className="text-white/70">{l.method}</span>
                    <span className="truncate">{l.family}{l.path}</span>
                    <span className="ml-auto shrink-0">{l.caller} · {l.ms}ms</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-sm text-slate-500">Caricamento stato…</div>
      )}
    </div>
  );
};
